import { fabric } from 'fabric';
import { saveAs } from 'file-saver';
import { getCursor } from './cursors';
import History from './History';
import { ToolFactory, modes } from './tools/toolFactory';

class Board {
  constructor(params) {
    const { canvasRef, drawingSettings, canvasConfig = {} } = params;
    this.canvas = null;
    this.canvasConfig = canvasConfig || {
      zoom: 1,
      contentJSON: null,
      minZoom: 0.05,
      maxZoom: 9.99,
      viewportTransform: [1, 0, 0, 1, 0, 0]
    };
    this.drawingSettings = drawingSettings;
    this.canvasRef = canvasRef;
    this.nowX = 0;
    this.nowY = 0;
    this.lastPosX = 0;
    this.lastPosY = 0;
    this.isPanning = false;
    this.history = null;
    this.toolFactory = null;
    this.currentTool = null;
    this.fileReaderInfo = null; // Add fileReaderInfo property
    
    // Global materials store
    this.globalMaterials = {
      // Default materials with their colors
      'Mulch': '#8B4513',
      'Pebbles': '#A9A9A9',
      'Concrete': '#C0C0C0',
      'Turf': '#228B22',
      'Pavers': '#D2691E'
    };
    
    // Context menu properties
    this.contextMenuVisible = false;
    this.contextMenuPosition = { x: 0, y: 0 };
    this.contextMenuCallback = null;

    // [Sketch range limits]
    this.limitScale = 10;
    const windowWidth = this.limitScale * (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth);
    const windowHeight = this.limitScale * (window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight);
    this.sketchWidthLimit = Math.max(1920 * this.limitScale, windowWidth);
    this.sketchHeightLimit = Math.max(1080 * this.limitScale, windowHeight);

    this.init();
  }

  init() {
    // Setup custom canvas methods if needed
    fabric.Canvas.prototype.getItemByAttr = function (attr, name) {
      var object = null,
        objects = this.getObjects();
      for (var i = 0, len = this.size(); i < len; i++) {
        if (objects[i][attr] && objects[i][attr] === name) {
          object = objects[i];
          break;
        }
      }
      return object;
    };
    
    // Get the canvas element directly
    const canvasElement = document.getElementById('canvas');
    if (!canvasElement) {
      console.error('Canvas element not found');
      return;
    }
    
    // Set willReadFrequently attribute on the 2D context
    // This improves performance when using getImageData frequently
    const ctx = canvasElement.getContext('2d', { willReadFrequently: true });
    
    // Get the parent element for sizing
    const parentElement = canvasElement.parentNode;
    if (!parentElement) {
      console.error('Canvas parent element not found');
      return;
    }
    
    const options = {
      preserveObjectStacking: true,
      selection: true, // Enable selection by default
      defaultCursor: getCursor('pencil'),
      backgroundColor: 'white',
      perPixelTargetFind: true,
      willReadFrequently: true,
      isDrawingMode: false,
      enableRetinaScaling: true,
      stopContextMenu: true,
      fireRightClick: true,
      fireMiddleClick: true,
      // Improve selection behavior
      targetFindTolerance: 4, // Make selection more forgiving
      selectionBorderColor: 'rgba(0,0,0,0.3)', // More subtle selection border
      selectionLineWidth: 1
    };
    
    // Create the canvas with the original options approach
    const canvas = new fabric.Canvas('canvas', {
      ...options,
      maxZoom: this.canvasConfig.maxZoom || 7,
      minZoom: this.canvasConfig.minZoom || 0.1,
      viewportTransform: this.canvasConfig.viewportTransform || [1, 0, 0, 1, 0, 0],
      ...(this.canvasConfig.settings || {}),
    });
    
    // Set up the canvas dimensions
    canvas.setWidth(parentElement.clientWidth);
    canvas.setHeight(parentElement.clientHeight);
    
    // Set up resize handler
    this.element = this.handleResize(this.resizeCanvas(canvas, parentElement).bind(this));
    if (typeof ResizeObserver !== 'undefined') {
      this.element.observe(parentElement);
    }

    this.canvas = canvas;
    this.history = new History(canvas);
    this.toolFactory = new ToolFactory(this);
    
    // Set up history change handler
    this.history.onHistoryChange = () => {
      // Ensure the canvas emits a history change event for listeners
      if (this.canvas && this.canvas.fire) {
        this.canvas.fire('history:changed');
      }
    };
    
    // Take an initial snapshot to establish base state after the canvas is fully initialized
    // Using requestAnimationFrame ensures we're in a stable state for the first snapshot
    requestAnimationFrame(() => {
      // Reset the history to start with a clean slate
      this.history.clear();
      
      // Take a snapshot of the initial canvas state
      const initialState = this.canvas.toJSON(['id', 'selectable', 'name']);
      const canvasJSON = JSON.stringify(initialState);
      this.history.history.push(canvasJSON);
      
      // Fire events to update UI
      if (this.canvas.fire) {
        this.canvas.fire('history:changed');
      }
      
      if (typeof this.history.onHistoryChange === 'function') {
        this.history.onHistoryChange();
      }
    });
    
    // Apply any saved canvas state
    if (this.canvasConfig.contentJSON) {
      canvas.loadFromJSON(this.canvasConfig.contentJSON, () => {
        canvas.renderAll();
      });
    }

    // Set up initial zoom
    if (this.canvasConfig.zoom) {
      canvas.setZoom(this.canvasConfig.zoom);
    }
    
    // Apply viewport transform if defined
    if (this.canvasConfig.viewportTransform) {
      canvas.viewportTransform = this.canvasConfig.viewportTransform;
    }

    this.setDrawingSettings(this.drawingSettings);
    this.setupCanvasEvents();

    // Initialize with SELECT mode if no mode set
    if (!this.drawingSettings.currentMode) {
      this.setMode(modes.SELECT);
    }
  }

  setupCanvasEvents() {
    const canvas = this.canvas;
    
    // Enable better touch support
    fabric.Object.prototype.cornerSize = 12; // Make touch targets larger

    // Setup pan events for mouse
    canvas.on('mouse:down', (opt) => {
      const evt = opt.e;
      if (evt.altKey === true) {
        this.lastPosX = evt.clientX;
        this.lastPosY = evt.clientY;
        this.isPanning = true;
      }
    });
    
    // Setup pan events for touch with alt key simulation using direct DOM events
    const touchStartHandler = (evt) => {
      // Check if we're using the pan tool - only then allow panning with single touch
      const isPanToolActive = this._currentMode === modes.PAN;
      
      // For pan tool or multi-finger touch (works in any tool mode)
      if ((isPanToolActive && evt.touches && evt.touches.length === 1) || 
          (evt.touches && evt.touches.length >= 2)) {
        
        evt.preventDefault(); // Prevent default pinch-zoom behavior
        
        const touch = evt.touches[0];
        this.lastPosX = touch.clientX;
        this.lastPosY = touch.clientY;
        this.isPanning = true;
      }
    };
    
    if (canvas.upperCanvasEl) {
      // Use passive: false to ensure preventDefault works
      canvas.upperCanvasEl.addEventListener('touchstart', touchStartHandler, { passive: false });
      
      // Store the handler for removal later
      this._touchStartHandler = touchStartHandler;
    }

    canvas.on('mouse:move', (opt) => {
      if (this.isPanning) {
        const e = opt.e;
        const vpt = canvas.viewportTransform;
        vpt[4] += e.clientX - this.lastPosX;
        vpt[5] += e.clientY - this.lastPosY;
        
        // Apply limits to prevent panning outside boundaries
        try {
          const scale = canvas.getZoom();
          vpt[4] = this.axisLimit({ scale, vpt: vpt[4], axis: 'x' });
          vpt[5] = this.axisLimit({ scale, vpt: vpt[5], axis: 'y' });
          
          // Save current position
          this.nowX = vpt[4];
          this.nowY = vpt[5];
        } catch (error) {
          console.log(error);
        }
        
        canvas.requestRenderAll();
        this.lastPosX = e.clientX;
        this.lastPosY = e.clientY;
      }
    });
    
    // Add a touch move handler that checks if panning is active
    const touchMoveHandler = (evt) => {
      if (this.isPanning && evt.touches) {
        evt.preventDefault();
        
        const touch = evt.touches[0];
        const vpt = canvas.viewportTransform;
        vpt[4] += touch.clientX - this.lastPosX;
        vpt[5] += touch.clientY - this.lastPosY;
        
        // Apply limits to prevent panning outside boundaries
        try {
          const scale = canvas.getZoom();
          vpt[4] = this.axisLimit({ scale, vpt: vpt[4], axis: 'x' });
          vpt[5] = this.axisLimit({ scale, vpt: vpt[5], axis: 'y' });
          
          // Save current position
          this.nowX = vpt[4];
          this.nowY = vpt[5];
        } catch (error) {
          console.log(error);
        }
        
        canvas.requestRenderAll();
        this.lastPosX = touch.clientX;
        this.lastPosY = touch.clientY;
      } else if (this._currentMode !== modes.PAN && evt.touches && evt.touches.length === 1) {
        // Prevent default touch behavior for non-pan tools with single touch
        // This stops the canvas from moving when using other tools
        evt.preventDefault();
      }
    };
    
    if (canvas.upperCanvasEl) {
      canvas.upperCanvasEl.addEventListener('touchmove', touchMoveHandler, { passive: false });
      this._touchMoveHandler = touchMoveHandler;
    }

    canvas.on('mouse:up', () => {
      this.isPanning = false;
    });
    
    // Handle touch end using direct DOM events
    const touchEndHandler = (evt) => {
      if (evt && this._currentMode !== modes.PAN && evt.touches && evt.touches.length === 0) {
        // Prevent default behavior for non-pan tools on touch end
        // This helps prevent unwanted navigation or bouncing effects
        evt.preventDefault();
      }
      this.isPanning = false;
    };
    
    if (canvas.upperCanvasEl) {
      canvas.upperCanvasEl.addEventListener('touchend', touchEndHandler, { passive: false });
      canvas.upperCanvasEl.addEventListener('touchcancel', touchEndHandler, { passive: false });
      
      // Store handlers for removal later
      this._touchEndHandler = touchEndHandler;
    }
    
    // Setup selection events for context panel
    canvas.on('selection:created', (opt) => {
      console.log('Selection created:', opt.selected);
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        console.log('Properties extracted:', selectedObject);
        
        // Only show context menu for polylines or when not in material calculator mode
        if (selectedObject.tool === 'polyline' || this._currentMode !== modes.MATERIAL_CALCULATOR) {
          this.contextMenuCallback({
            visible: true,
            x: 0,
            y: 0,
            selectedObject
          });
        }
      }
    });
    
    canvas.on('selection:updated', (opt) => {
      console.log('Selection updated:', opt.selected);
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        console.log('Properties extracted:', selectedObject);
        
        // Only show context menu for polylines or when not in material calculator mode
        if (selectedObject.tool === 'polyline' || this._currentMode !== modes.MATERIAL_CALCULATOR) {
          this.contextMenuCallback({
            visible: true,
            x: 0,
            y: 0,
            selectedObject
          });
        }
      }
    });
    
    canvas.on('selection:cleared', (opt) => {
      // Only clear selection if we're not in material calculator mode
      if (this._currentMode !== modes.MATERIAL_CALCULATOR) {
        console.log('Selection cleared');
        if (this.contextMenuCallback) {
          this.contextMenuCallback({
            visible: false,
            x: 0,
            y: 0,
            selectedObject: null
          });
        }
      }
    });
    
    // Add handlers for object modifications to record history
    // Use a single event handler for all object modification events
    const objectChangeHandler = (eventType) => {
      // Skip if we're in the middle of a polyline drawing
      if (this._isPolylineDrawingInProgress()) {
        return;
      }
      
      // Skip if snapshots are explicitly disabled
      if (this.history && this.history._disableSnapshots) {
        return;
      }
      
      // Reduce excessive logging
      if (process.env.NODE_ENV !== 'production') {
        console.log(`Object ${eventType}, taking history snapshot`);
      }
      
      this.history.snapshot();
    };
    
    // Helper method to check if a polyline drawing is in progress
    this._isPolylineDrawingInProgress = () => {
      // If we don't have a tool factory, we can't check
      if (!this.toolFactory) {
        return false;
      }
      
      // Get the current mode and check if it's POLYLINE
      const currentMode = this.getMode();
      if (currentMode === 'POLYLINE') {
        const polylineTool = this.toolFactory.getTool('POLYLINE');
        // If the tool is drawing but not complete, we're in the middle of a drawing
        if (polylineTool && polylineTool.isDrawing && !polylineTool.isDrawingComplete) {
          return true;
        }
      }
      
      return false;
    };
    
    // Attach the same handler to all modification events
    canvas.on('object:modified', () => objectChangeHandler('modified'));
    canvas.on('object:added', () => objectChangeHandler('added'));
    canvas.on('object:removed', () => objectChangeHandler('removed'));
    
    // Setup context menu (right-click) - keeping for backwards compatibility
    canvas.on('contextmenu', (opt) => {
      // Prevent the browser's context menu
      opt.e.preventDefault();
    });

    // Setup zoom events - use the original approach
    canvas.on('mouse:wheel', (opt) => {
      opt.e.preventDefault();
      opt.e.stopPropagation();
      
      const delta = opt.e.deltaY;
      let zoom = canvas.getZoom();
      zoom *= 0.999 ** delta;
      if (zoom > this.canvasConfig.maxZoom) zoom = this.canvasConfig.maxZoom;
      if (zoom < this.canvasConfig.minZoom) zoom = this.canvasConfig.minZoom;
      canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      
      const data = {
        scale: zoom,
      };
      canvas.fire('zoom:change', data);
    });
  }

  removeBoard() {
    // Clean up touch event listeners to prevent memory leaks
    if (this.canvas && this.canvas.upperCanvasEl) {
      // Remove touch start handler
      if (this._touchStartHandler) {
        this.canvas.upperCanvasEl.removeEventListener('touchstart', this._touchStartHandler, { passive: false });
      }
      
      // Remove touch move handler
      if (this._touchMoveHandler) {
        this.canvas.upperCanvasEl.removeEventListener('touchmove', this._touchMoveHandler, { passive: false });
      }
      
      // Remove touch end handlers
      if (this._touchEndHandler) {
        this.canvas.upperCanvasEl.removeEventListener('touchend', this._touchEndHandler, { passive: false });
        this.canvas.upperCanvasEl.removeEventListener('touchcancel', this._touchEndHandler, { passive: false });
      }
    }
    
    if (this.canvas) {
      this.canvas.dispose();
    }
  }

  setCanvasConfig(canvasConfig) {
    this.canvasConfig = canvasConfig;
  }
  
  /**
   * Set file reader info and filter objects by page
   * @param {Object} fileReaderInfo - The file reader information
   */
  setFileReaderInfo(fileReaderInfo) {
    this.fileReaderInfo = fileReaderInfo;
    
    // Filter objects by page
    if (this.toolFactory && this.canvas && fileReaderInfo?.file?.name) {
      // Store current scale ratio to ensure it's preserved
      const currentScaleRatio = this.canvas.scaleRatio;
      
      const polylineTool = this.toolFactory.getTool('POLYLINE');
      if (polylineTool) {
        // Make sure the scale ratio is set on the canvas before filtering
        if (currentScaleRatio) {
          this.canvas.scaleRatio = currentScaleRatio;
        }
        
        polylineTool.filterInfoWindowsByPage(
          fileReaderInfo.currentPageNumber,
          fileReaderInfo.file.name
        );
      }
    }
  }

  setDrawingSettings(drawingSettings) {
    this.drawingSettings = drawingSettings;
    
    // Activate the current mode if drawingSettings has currentMode
    if (drawingSettings.currentMode) {
      this.setMode(drawingSettings.currentMode);
    }
    
    // Make sure brush settings are applied
    if (this.canvas) {
      if (this.canvas.freeDrawingBrush) {
        this.canvas.freeDrawingBrush.width = drawingSettings.brushWidth || 1;
        this.canvas.freeDrawingBrush.color = drawingSettings.currentColor || '#000000';
      }
    }
  }

  setMode(mode) {
    if (!Object.values(modes).includes(mode)) {
      console.error(`Invalid mode: ${mode}`);
      return;
    }

    // Store the currently selected object if it's a polyline
    const activeObject = this.canvas?.getActiveObject();
    const wasPolyline = activeObject?.polyline;
    const lastSelectedId = wasPolyline ? activeObject.id : null;

    // Set the current mode in drawing settings and store it explicitly on the board
    this.drawingSettings.currentMode = mode;
    this._currentMode = mode; // Store current mode explicitly on the board
    
    console.log(`Setting mode to: ${mode}`);

    // Reset the canvas for the new tool
    this.resetCanvas(mode);

    // Activate the tool
    this.currentTool = this.toolFactory.activateTool(mode);

    // If switching to SELECT mode and we had a polyline selected, reselect it
    if (mode === modes.SELECT && lastSelectedId) {
      // Find the object by ID
      const objects = this.canvas.getObjects();
      const objectToSelect = objects.find(obj => obj.id === lastSelectedId);
      if (objectToSelect) {
        // Use setTimeout to ensure this happens after mode change is complete
        setTimeout(() => {
          this.canvas.setActiveObject(objectToSelect);
          this.canvas.requestRenderAll();
        }, 0);
      }
    }
  }
  
  resetCanvas(newMode) {
    const canvas = this.canvas;
    
    // Make sure we're not interfering with the tool's own event handlers
    if (this.currentTool) {
      this.currentTool.deactivate();
    }
    
    // Reset canvas settings based on mode
    canvas.isDrawingMode = false;
    canvas.defaultCursor = 'auto';
    canvas.hoverCursor = 'auto';
    
    // Set selection based on mode
    if (newMode === modes.SELECT || newMode === modes.MATERIAL_CALCULATOR) {
      canvas.selection = true;
      // Make only polylines selectable in these modes
      canvas.getObjects().forEach(item => {
        item.set({ selectable: item.polyline === true });
      });
    } else {
      canvas.selection = false;
      // Make all objects non-selectable in other modes
      canvas.getObjects().forEach(item => {
        item.set({ selectable: false });
      });
    }
    
    // Only clear selection if not switching to SELECT or MATERIAL_CALCULATOR mode
    if (newMode !== modes.SELECT && newMode !== modes.MATERIAL_CALCULATOR) {
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
  }

  getMode() {
    return this.drawingSettings.currentMode;
  }

  clear() {
    this.canvas.clear();
    this.history.snapshot();
  }

  clearCanvas() {
    // Store measurement objects (polygons and info windows) before clearing
    const measurementObjects = this.canvas.getObjects().filter(obj => {
      return (obj.type === 'polygon' && obj.scaleRatio !== undefined) || 
             (obj instanceof fabric.Group && obj.polygonId);
    });
    
    // Keep track of the current scaleRatio before clearing
    const currentScaleRatio = this.canvas.scaleRatio;
    
    // Clear the canvas
    this.canvas.clear();
    
    // Restore the scaleRatio after clearing
    this.canvas.scaleRatio = currentScaleRatio;
    
    // If we have the polyline tool available and file info
    if (this.toolFactory && this.fileReaderInfo) {
      // Re-add all measurement objects to the canvas
      measurementObjects.forEach(obj => {
        // Ensure objects have the current scale ratio
        if (obj.type === 'polygon' && obj.scaleRatio !== undefined) {
          obj.scaleRatio = currentScaleRatio;
        }
        this.canvas.add(obj);
      });
      
      // Get the polyline tool to filter objects by current page
      const polylineTool = this.toolFactory.getTool('POLYLINE');
      if (polylineTool) {
        polylineTool.filterInfoWindowsByPage(
          this.fileReaderInfo.currentPageNumber, 
          this.fileReaderInfo.file.name
        );
      }
    }
  }

  openPage(page) {
    // Store measurement objects (polygons and info windows) before changing page
    const measurementObjects = this.canvas.getObjects().filter(obj => {
      return (obj.type === 'polygon' && obj.scaleRatio !== undefined) || 
             (obj instanceof fabric.Group && obj.polygonId);
    });
    
    // Keep track of the current scaleRatio before clearing
    const currentScaleRatio = this.canvas.scaleRatio;
    
    if (page) {
      // Create an AbortController to handle TextLayer cancellation gracefully
      if (window.AbortController && !this._pageAbortController) {
        this._pageAbortController = new AbortController();
      } else if (this._pageAbortController) {
        // Abort previous TextLayer tasks to prevent warnings
        this._pageAbortController.abort();
        this._pageAbortController = new AbortController();
      }
      
      // Clear the canvas
      this.canvas.clear();
      
      // Restore the scaleRatio after clearing
      this.canvas.scaleRatio = currentScaleRatio;
      
      // Set the background image
      this.canvas.setBackgroundImage(page, this.canvas.renderAll.bind(this.canvas));
      
      // Re-add all measurement objects
      measurementObjects.forEach(obj => {
        // Ensure objects have the current scale ratio
        if (obj.type === 'polygon' && obj.scaleRatio !== undefined) {
          obj.scaleRatio = currentScaleRatio;
        }
        this.canvas.add(obj);
      });
      
      // Filter objects by page if we have the tools and page info
      if (this.toolFactory && this.fileReaderInfo) {
        const polylineTool = this.toolFactory.getTool('POLYLINE');
        if (polylineTool) {
          polylineTool.filterInfoWindowsByPage(
            this.fileReaderInfo.currentPageNumber,
            this.fileReaderInfo.file.name
          );
        }
      }
    } else {
      this.clearCanvas();
    }
  }

  download(fileName) {
    // create a new canvas element
    const multiplier = 2; // for better quality
    const tempCanvas = document.createElement('canvas');
    const tempContext = tempCanvas.getContext('2d');

    // Set the tempCanvas dimensions to match the real canvas
    tempCanvas.width = this.canvas.width * multiplier;
    tempCanvas.height = this.canvas.height * multiplier;

    // Draw a white background
    tempContext.fillStyle = 'white';
    tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Set the background color from drawingSettings if available
    if (this.drawingSettings.backgroundColor) {
      tempContext.fillStyle = this.drawingSettings.backgroundColor;
      tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    }

    // Scale and draw the current canvas on the temp canvas for higher resolution
    tempContext.scale(multiplier, multiplier);
    this.canvas.renderAll();
    tempContext.drawImage(
      this.canvas.getElement(),
      0,
      0,
      this.canvas.width,
      this.canvas.height,
    );

    // Generate the image data
    tempCanvas.toBlob((blob) => {
      saveAs(blob, fileName);
    });
  }

  // Pan and zoom methods
  changeZoom({ scale }) {
    const canvas = this.canvas;
    let zoom = canvas.getZoom();
    zoom *= scale;

    if (zoom > this.canvasConfig.maxZoom) zoom = this.canvasConfig.maxZoom;
    if (zoom < this.canvasConfig.minZoom) zoom = this.canvasConfig.minZoom;

    canvas.setZoom(zoom);

    const data = {
      scale: zoom,
    };
    canvas.fire('zoom:change', data);

    return zoom;
  }

  resetZoom(scale) {
    const canvas = this.canvas;

    if (scale > this.canvasConfig.maxZoom) scale = this.canvasConfig.maxZoom;
    if (scale < this.canvasConfig.minZoom) scale = this.canvasConfig.minZoom;

    canvas.setZoom(scale);

    const data = {
      scale,
    };
    canvas.fire('zoom:change', data);

    return scale;
  }

  // Persist methods for the History component
  redo() {
    if (!this.history) return false;
    try {
      this.history.redo();
      return true;
    } catch (error) {
      console.error('Error in Board.redo():', error);
      return false;
    }
  }

  undo() {
    if (!this.history) return false;
    try {
      this.history.undo();
      return true;
    } catch (error) {
      console.error('Error in Board.undo():', error);
      return false;
    }
  }
  
  // Check if undo is available
  canUndo() {
    return this.history && this.history.getHistoryLength() > 0;
  }
  
  // Check if redo is available
  canRedo() {
    return this.history && this.history.getRedoHistoryLength() > 0;
  }
  
  // Delete the currently selected object(s)
  deleteSelectedObjects() {
    if (!this.canvas) return;
    
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    console.log('Deleting selected object(s)');
    
    // If it's a multi-selection, remove all selected objects
    if (activeObject.type === 'activeSelection' && activeObject._objects) {
      const objects = activeObject._objects.slice(); // Create a copy to avoid modification issues
      this.canvas.discardActiveObject();
      objects.forEach(obj => this.canvas.remove(obj));
    } else {
      // Single object deletion
      this.canvas.remove(activeObject);
    }
    
    this.canvas.requestRenderAll();
    this.history.snapshot();
    
    return true; // Return true to indicate that something was deleted
  }

  cancelDrawing() {
    if (this.currentTool) {
      this.currentTool.stopDrawing();
    }
  }

  // Method for calculating real-world distance (used by measurement tools)
  calculateRealDistance(x1, y1, x2, y2) {
    const pixelDistance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    if (this.canvas.scaleRatio) {
      return pixelDistance / this.canvas.scaleRatio;
    }
    return pixelDistance; // Return pixel distance if no scale is set
  }
  
  // Limits for panning to keep within canvas boundaries
  axisLimit({ scale, vpt, axis }) {
    // Get the appropriate settings for the axis
    const axisSettings = {
      x: {
        size: this.sketchWidthLimit,
        canvasSize: this.canvas.width,
      },
      y: {
        size: this.sketchHeightLimit,
        canvasSize: this.canvas.height,
      },
    }[axis];
    
    // For a very large area, we'll use less restrictive limits
    const absSize = axisSettings.size * scale;
    
    // Determine min and max values for the viewport transform
    // We're giving more room to pan by multiplying by 0.5 instead of 0.3
    const min = -(absSize - axisSettings.canvasSize * scale) - absSize * 0.5;
    const max = absSize * 0.5;
    
    return Math.min(Math.max(vpt, min), max);
  }
  
  // Helper for getting canvas element dimensions
  resizeCanvas(canvas, parent) {
    return function() {
      // Store current zoom and viewport transform
      const zoom = canvas.getZoom();
      const vpt = [...canvas.viewportTransform];
      
      // Update dimensions
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      
      // Update canvas dimensions
      canvas.setWidth(width);
      canvas.setHeight(height);
      
      // Restore zoom and viewport transform
      canvas.setZoom(zoom);
      canvas.setViewportTransform(vpt);
      
      // Force re-render
      canvas.renderAll();
    };
  }
  
  // Throttle function to prevent too many resize events
  throttle(func, delay = 300) {
    let timer = 0;
    return function() {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  }
  
  // Set the canvas size and maintain zoom and viewport transform
  setCanvasSize(width, height) {
    // Store current zoom and viewport transform
    const zoom = this.canvas.getZoom();
    const vpt = [...this.canvas.viewportTransform];
    
    // Update dimensions
    this.canvas.setWidth(width);
    this.canvas.setHeight(height);
    
    // Restore zoom and viewport transform
    this.canvas.setZoom(zoom);
    this.canvas.setViewportTransform(vpt);
    
    // Force re-render
    this.canvas.requestRenderAll();
  }
  
  // Handle resize events with improved debouncing
  handleResize(callback) {
    if (typeof ResizeObserver !== 'undefined') {
      return new ResizeObserver(this.throttle(callback, 150)); // Reduced delay for more responsive resizing
    }
    return { observe: () => {} }; // Fallback
  }
  
  // Context panel control methods
  setContextMenuCallback(callback) {
    this.contextMenuCallback = callback;
  }
  
  closeContextMenu() {
    if (this.contextMenuCallback) {
      this.contextMenuCallback({ visible: false, x: 0, y: 0, selectedObject: null });
    }
  }
  
  // Get properties from the active object
  getSelectedObjectProperties() {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) {
      console.log('No active object found');
      return null;
    }
    
    console.log('Getting properties for object:', activeObject.type, activeObject);
    
    try {
      // Basic properties all objects have
      const properties = {
        type: activeObject.type,
        tool: activeObject.tool || '',
        width: activeObject.width || 0,
        height: activeObject.height || 0,
        left: activeObject.left || 0,
        top: activeObject.top || 0,
        angle: activeObject.angle || 0
      };
      
      // Check for explicit tool type first
      if (activeObject.tool) {
        properties.tool = activeObject.tool;
        console.log('Found explicit tool property:', properties.tool);
      }
      
      // Special case for polyline objects
      if (activeObject.type === 'polygon' || activeObject.polyline === true) {
        properties.tool = 'polyline';
        // Also capture material property if it exists
        if (activeObject.material) {
          properties.material = activeObject.material;
        }
        console.log('Detected polyline/polygon object');
      }
      
      // Style properties
      properties.stroke = activeObject.stroke || '#000000';
      properties.fill = activeObject.fill || 'transparent';
      properties.strokeWidth = activeObject.strokeWidth || 1;
      properties.opacity = activeObject.opacity !== undefined ? activeObject.opacity : 1;
      properties.hasFill = activeObject.fill && activeObject.fill !== 'transparent';
      
      // Handle composite/group objects
      if (activeObject.type === 'group' && activeObject._objects) {
        console.log('Processing group with', activeObject._objects.length, 'objects');
        // Find the special object within the group that might have properties we need
        const containedObjects = activeObject._objects || [];
        
        // Look for tool property in any contained object
        for (let obj of containedObjects) {
          if (obj.tool) {
            properties.tool = obj.tool;
            console.log('Found tool property in contained object:', properties.tool);
            break;
          }
        }
        
        // Special case for polyline groups - look at all objects
        if (activeObject.polyline === true || 
            containedObjects.some(obj => obj.type === 'polygon' || obj.polyline === true)) {
          properties.tool = 'polyline';
          
          // Look for material property in the group or any contained polyline
          if (activeObject.material) {
            properties.material = activeObject.material;
          } else {
            // Check if any contained object has material property
            for (let obj of containedObjects) {
              if (obj.material && (obj.type === 'polygon' || obj.polyline === true)) {
                properties.material = obj.material;
                break;
              }
            }
          }
          
          console.log('Detected polyline group');
        }
        
        // Look for scale and dimension objects in the group
        if (containedObjects.some(obj => obj.scaleData)) {
          properties.tool = 'scale';
          console.log('Detected scale tool group');
        }
        
        if (containedObjects.some(obj => obj.measurementData)) {
          properties.tool = 'dimension';
          console.log('Detected dimension tool group');
        }
        
        // Get style properties from the first object that has them
        for (let obj of containedObjects) {
          if (obj.stroke && !properties.stroke) {
            properties.stroke = obj.stroke;
          }
          if (obj.fill && obj.fill !== 'transparent' && !properties.hasFill) {
            properties.fill = obj.fill;
            properties.hasFill = true;
          }
          if (obj.strokeWidth && !properties.strokeWidth) {
            properties.strokeWidth = obj.strokeWidth;
          }
          if (obj.opacity !== undefined && properties.opacity === undefined) {
            properties.opacity = obj.opacity;
          }
          
          // Collect measurement data from group objects
          if (obj.measurements) {
            properties.measurements = { ...obj.measurements };
          }
          if (obj.measurementData) {
            properties.measurementData = { ...obj.measurementData };
          }
          if (obj.scaleData) {
            properties.scaleData = { ...obj.scaleData };
          }
        }
      }
      
      // For polyline objects with measurement info
      if (activeObject.measurements) {
        properties.measurements = { ...activeObject.measurements };
      } else if (properties.tool === 'polyline') {
        // Try to find measurement data in polyline
        const polylineTool = this.toolFactory?.getTool('POLYLINE');
        if (polylineTool) {
          const measurements = polylineTool.getMeasurementsForObject(activeObject);
          if (measurements) {
            // Add measurements to the properties object
            properties.measurements = measurements;
            
            // Also add direct access to area and perimeter for the context panel
            if (measurements.area !== undefined) {
              properties.area = measurements.area;
            }
            if (measurements.perimeter !== undefined) {
              properties.perimeter = measurements.perimeter;
            }
            if (measurements.unit !== undefined) {
              properties.unit = measurements.unit;
            }
          }
        }
      }
      
      // Also directly use values stored on the object itself
      if (activeObject.area !== undefined) {
        properties.area = activeObject.area;
      }
      if (activeObject.perimeter !== undefined) {
        properties.perimeter = activeObject.perimeter;
      }
      if (activeObject.unit !== undefined) {
        properties.unit = activeObject.unit;
      }
      
      // For dimension objects
      if (activeObject.measurementData || properties.tool === 'dimension') {
        properties.measurementData = activeObject.measurementData || {};
      }
      
      // For scale tool objects
      if (activeObject.scaleData || properties.tool === 'scale') {
        properties.scaleData = activeObject.scaleData || {};
      }
      
      // Determine if object supports editing of properties
      properties.canEdit = {
        stroke: activeObject.stroke !== undefined,
        fill: activeObject.fill !== undefined && 
              (properties.tool !== 'dimension') && 
              (activeObject.type === 'path' || activeObject.type === 'rect' || 
               activeObject.type === 'circle' || activeObject.type === 'ellipse' || 
               activeObject.type === 'polygon'),
        strokeWidth: activeObject.strokeWidth !== undefined,
        opacity: true, // Most objects support opacity
        // Add material property for polyline objects
        material: properties.tool === 'polyline'
      };
      
      // Manually handle special cases for different tools
      if (properties.tool === 'dimension') {
        properties.canEdit.fill = false;
      }
      
      console.log('Selected object properties:', properties);
      return properties;
    } catch (error) {
      console.error('Error extracting object properties:', error);
      return {
        type: activeObject.type || 'unknown',
        tool: activeObject.tool || ''
      };
    }
  }
  
  // Context panel callback for applying changes from the UI
  getContextPanelCallback = () => {
    // Get currently selected object
    const activeObject = this.canvas.getActiveObject();
    
    if (activeObject) {
      console.log('Creating context panel callback for object:', activeObject.type);
      
      // Create a callback function that will apply changes from context panel
      return (changes) => {
        console.log('Context panel callback with changes:', changes);
        
        try {
          // Ensure the object is still selected
          if (this.canvas.getActiveObject() !== activeObject) {
            this.canvas.setActiveObject(activeObject);
          }
          
          // Handle objects based on their type
          if (activeObject.type === 'group') {
            // For group objects, apply properties to all child objects as needed
            if (activeObject._objects) {
              activeObject._objects.forEach(obj => {
                this.applyChangesToObject(obj, changes);
              });
            }
            
            // Also apply to the group itself for certain properties
            this.applyChangesToObject(activeObject, changes);
            
          } else {
            // For individual objects apply directly
            this.applyChangesToObject(activeObject, changes);
          }
          
          // Create a snapshot for history
          this.history.snapshot();
          
          // Request a render to show changes
          this.canvas.requestRenderAll();
          
          // Update context panel with new properties
          if (this.contextMenuCallback) {
            const selectedObject = this.getSelectedObjectProperties();
            this.contextMenuCallback({
              visible: true,
              x: 0,
              y: 0,
              selectedObject
            });
          }
        } catch (error) {
          console.error('Error in context panel callback:', error);
        }
      };
    }
    
    return null;
  };
  
  // Object layering methods
  bringSelectedForward() {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.bringForward();
      this.canvas.requestRenderAll();
      this.history.snapshot();
    }
  }
  
  sendSelectedBackward() {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.sendBackwards();
      this.canvas.requestRenderAll();
      this.history.snapshot();
    }
  }
  
  bringSelectedToFront() {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.bringToFront();
      this.canvas.requestRenderAll();
      this.history.snapshot();
    }
  }
  
  sendSelectedToBack() {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.sendToBack();
      this.canvas.requestRenderAll();
      this.history.snapshot();
    }
  }
  
  // Object property modification methods
  
  // Helper method to apply changes to an individual object
  applyChangesToObject(obj, changes) {
    if (!obj) return;
    
    try {
      // Apply stroke color if changed
      if (changes.color && obj.stroke) {
        console.log(`Applying stroke color: ${changes.color} to object type: ${obj.type}`);
        obj.set('stroke', changes.color);
      }
      
      // Apply fill if toggled or color changed
      if (changes.toggleFill !== undefined) {
        console.log(`Toggling fill for object type: ${obj.type}`);
        if (obj.fill && obj.fill !== 'transparent') {
          // If object has fill, make it transparent
          obj.set('fill', 'transparent');
        } else if (obj.stroke) {
          // If object doesn't have fill, set it to stroke color
          obj.set('fill', obj.stroke);
        }
      } else if (changes.color && obj.fill && obj.fill !== 'transparent') {
        // Apply color to fill only if object already has a fill
        console.log(`Applying fill color: ${changes.color} to object type: ${obj.type}`);
        obj.set('fill', changes.color);
      }
      
      // Apply stroke width if changed
      if (changes.width && obj.strokeWidth !== undefined) {
        console.log(`Applying stroke width: ${changes.width} to object type: ${obj.type}`);
        obj.set('strokeWidth', changes.width);
      }
      
      // Apply opacity if changed
      if (changes.opacity !== undefined) {
        const opacityValue = changes.opacity / 100; // Convert from 0-100 to 0-1
        console.log(`Applying opacity: ${opacityValue} to object type: ${obj.type}`);
        obj.set('opacity', opacityValue);
      }
      
      // Apply material if changed (for polyline objects)
      if (changes.material !== undefined && obj.polyline) {
        console.log(`Applying material: ${changes.material} to polyline object`);
        obj.set('material', changes.material);
        
        // If fill color is provided with material, apply it
        if (changes.fill) {
          console.log(`Applying fill color: ${changes.fill} based on material`);
          obj.set('fill', changes.fill);
        }
      }
    } catch (error) {
      console.error('Error applying changes to object:', error);
    }
  }
  
  changeSelectedObjectColor(color) {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    try {
      if (activeObject.type === 'group') {
        // For grouped objects, change all objects in the group
        activeObject._objects.forEach(obj => {
          if (obj.stroke) obj.set('stroke', color);
          if (obj.fill && obj.get('fill') !== 'transparent') obj.set('fill', color);
        });
      } else {
        // For single objects
        if (activeObject.stroke) activeObject.set('stroke', color);
        if (activeObject.fill && activeObject.get('fill') !== 'transparent') activeObject.set('fill', color);
      }
      
      this.canvas.requestRenderAll();
      this.history.snapshot();
      
      // Update the context menu
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        this.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error changing object color:', error);
    }
  }
  
  toggleSelectedObjectFill() {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    try {
      const currentFill = activeObject.get('fill');
      const currentStroke = activeObject.get('stroke');
      
      if (activeObject.type === 'group') {
        // For grouped objects
        activeObject._objects.forEach(obj => {
          const shouldFill = obj.fill === 'transparent' || !obj.fill;
          if (shouldFill && obj.stroke) {
            obj.set('fill', obj.stroke);
          } else {
            obj.set('fill', 'transparent');
          }
        });
      } else {
        // For single objects
        const shouldFill = currentFill === 'transparent' || !currentFill;
        if (shouldFill && currentStroke) {
          activeObject.set('fill', currentStroke);
        } else {
          activeObject.set('fill', 'transparent');
        }
      }
      
      this.canvas.requestRenderAll();
      this.history.snapshot();
      
      // Update the context menu
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        this.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error toggling object fill:', error);
    }
  }
  
  changeSelectedObjectWidth(width) {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    try {
      if (activeObject.type === 'group') {
        // For grouped objects
        activeObject._objects.forEach(obj => {
          if (obj.strokeWidth !== undefined) obj.set('strokeWidth', width);
        });
      } else {
        // For single objects
        if (activeObject.strokeWidth !== undefined) activeObject.set('strokeWidth', width);
      }
      
      this.canvas.requestRenderAll();
      this.history.snapshot();
      
      // Update the context menu
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        this.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error changing object width:', error);
    }
  }
  
  changeSelectedObjectOpacity(opacity) {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    try {
      // IMPORTANT: The value from ContextPanel is already in 0-1 range (divided by 100)
      // We just need to ensure it's within valid bounds
      const opacityValue = Math.max(0.05, Math.min(1, opacity));
      console.log('Board.changeSelectedObjectOpacity receiving:', opacity, 'applying:', opacityValue);
      
      if (activeObject.type === 'group') {
        // For grouped objects
        activeObject._objects.forEach(obj => {
          obj.set('opacity', opacityValue);
        });
        // Also set the group opacity
        activeObject.set('opacity', opacityValue);
      } else {
        // For single objects
        activeObject.set('opacity', opacityValue);
      }
      
      this.canvas.requestRenderAll();
      this.history.snapshot();
      
      // Update the context menu
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        this.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error changing object opacity:', error);
    }
  }
  
  // Set the material and its color for the selected object
  setSelectedObjectMaterial(material, color) {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    try {
      // First, add the material to our global store if it's not already there
      // This ensures all materials are available for any polyline
      this.addMaterial(material, color);
      
      if (activeObject.type === 'group') {
        // For grouped objects
        activeObject._objects.forEach(obj => {
          if (obj.polyline) {
            obj.set({
              'material': material,
              'fill': color,
              'opacity': obj.opacity // Preserve the existing opacity
            });
          }
        });
      } else if (activeObject.polyline) {
        // For single polyline objects
        activeObject.set({
          'material': material,
          'fill': color,
          'opacity': activeObject.opacity // Preserve the existing opacity
        });
      }
      
      this.canvas.requestRenderAll();
      this.history.snapshot();
      
      // Update the context menu
      if (this.contextMenuCallback) {
        const selectedObject = this.getSelectedObjectProperties();
        this.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error changing object material:', error);
    }
  }
  
  // Get all available materials (for reuse across polylines)
  getMaterials() {
    return this.globalMaterials;
  }
  
  // Add a new material to the global store
  addMaterial(name, color) {
    if (!this.globalMaterials[name]) {
      this.globalMaterials[name] = color;
      return true;
    }
    return false; // Material with this name already exists
  }
}

export { Board, modes };
